import { Box, Button, Divider, Stack, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PsychologyOutlinedIcon from "@mui/icons-material/PsychologyOutlined";

const TEAL = "#2D5A57";
const MINT_BG = "#F4F9F8";
const QUOTE_GRAY = "#616161";

interface NoMatchesViewProps {
  onRefineSearch: () => void;
}

export function NoMatchesView({ onRefineSearch }: NoMatchesViewProps) {
  return (
    <Box
      sx={{
        bgcolor: MINT_BG,
        width: "100%",
        minHeight: "100%",
        py: { xs: 3, md: 4 },
        px: { xs: 2, md: 3 },
      }}
    >
      <Stack spacing={4} sx={{ maxWidth: 800, mx: "auto", width: "100%" }} alignItems="center">
        <Stack spacing={1.5} alignItems="center" textAlign="center" px={1}>
          <Typography variant="h4" fontWeight={700} color="#212121">
            We weren't able to find a provider who matches your needs right now.
          </Typography>
          <Typography variant="body1" color="#424242" maxWidth={640}>
            Thank you for completing the Find a Provider form. We reviewed our current network of
            providers, but we weren't able to find a match for all of your selected criteria.
          </Typography>
        </Stack>

        <Box
          sx={{
            width: "100%",
            bgcolor: "#F8FAFA",
            border: "1px solid #E0E0E0",
            borderRadius: 2,
            p: { xs: 3, md: 4 },
          }}
        >
          <Typography variant="h6" fontWeight={700} color="#212121" mb={1}>
            Refine your search for better results
          </Typography>
          <Typography variant="body1" color="#424242" mb={3}>
            Please consider resubmitting your form with more flexible criteria to help us find a
            better match for your health journey.
          </Typography>
          <Button
            variant="contained"
            endIcon={<ArrowForwardIcon />}
            onClick={onRefineSearch}
            disableElevation
            sx={{
              bgcolor: TEAL,
              color: "#fff",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 10,
              px: 3,
              flexDirection: "row",
              gap: 1,
              "&:hover": { bgcolor: "#244A47" },
              "& .MuiSvgIcon-root": { fontSize: 18 },
            }}
          >
            Refine My Search
          </Button>
        </Box>

        <Box
          sx={{
            width: "100%",
            bgcolor: "#F8FAFA",
            border: "1px solid #E0E0E0",
            borderRadius: 2,
            p: { xs: 3, md: 4 },
            textAlign: "center",
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              border: "2px solid #9E9E9E",
              bgcolor: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2,
            }}
          >
            <PsychologyOutlinedIcon sx={{ color: "#757575", fontSize: 28 }} />
          </Box>
          <Typography variant="body1" fontStyle="italic" color={QUOTE_GRAY} mb={1.5}>
            "Your health journey is unique. While we don't have an immediate match today, our
            network is constantly expanding with providers who specialize in neuroplasticity and
            holistic wellness."
          </Typography>
          <Typography variant="body2" color={QUOTE_GRAY}>
            — The Unyte Health Team
          </Typography>
        </Box>

        <Divider sx={{ width: "100%" }} />

        <Stack spacing={0.5} alignItems="center">
          <Typography
            variant="caption"
            sx={{ color: "#757575", letterSpacing: 1, textTransform: "uppercase", fontWeight: 600 }}
          >
            Questions or need help?
          </Typography>
          <Typography
            component="a"
            href="mailto:clientconnections@unyte.com"
            variant="body1"
            fontWeight={700}
            sx={{ color: TEAL, textDecoration: "none" }}
          >
            clientconnections@unyte.com
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
}
